// src/pages/user/CSThanhToan.tsx
import React, { useEffect } from 'react';
import styled, { ThemeProvider, DefaultTheme } from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';

// --- THEME VÀ TYPES (Giữ nguyên) ---
const theme = {
  colors: {
    background: '#f8f9fa',
    surface: '#ffffff',
    primary: '#38a169',
    text: '#4a5568',
    heading: '#1a202c',
    subtleText: '#718096',
  },
  fonts: {
    body: "'Inter', sans-serif",
  },
  fontSizes: {
    base: '1rem',
    h1: '2.5rem',
    h2: '1.8rem',
    h3: '1.3rem',
  },
  spacing: {
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  shadows: {
    main: '0 8px 30px rgba(0, 0, 0, 0.08)',
  },
  borderRadius: '12px',
  breakpoints: {
    tablet: '768px',
  },
};

type AppTheme = typeof theme;

declare module 'styled-components' {
  export interface DefaultTheme extends AppTheme {}
}

// --- STYLED COMPONENTS (Giữ nguyên) ---
const PageContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xxl} 15px;
  font-family: ${({ theme }) => theme.fonts.body};
  min-height: 100vh;
`;

const ContentCard = styled.div`
  max-width: 850px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius};
  padding: ${({ theme }) => theme.spacing.xxl};
  box-shadow: ${({ theme }) => theme.shadows.main};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.8;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const Heading = styled.h2`
  margin-top: ${({ theme }) => theme.spacing.xxl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.heading};
  font-size: ${({ theme }) => theme.fontSizes.h2};
  font-weight: 700;
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  line-height: 1.4;

  &:first-of-type {
    margin-top: 0;
  }
`;

const SubHeading = styled.h3`
  margin-top: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.heading};
  font-size: ${({ theme }) => theme.fontSizes.h3};
  font-weight: 700;
  line-height: 1.5;
`;

const Paragraph = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.base};
`;

const List = styled.ul`
  list-style: none;
  padding-left: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ListItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: flex-start;
  font-size: ${({ theme }) => theme.fontSizes.base};

  &::before {
    content: '✓';
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
    margin-right: 12px;
    font-size: 1.1em;
    margin-top: 1px;
    text-align: center; 
  }
`;

const InfoBlock = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  margin: ${({ theme }) => theme.spacing.lg} 0;
  border-radius: 0 ${({ theme }) => theme.borderRadius} ${({ theme }) => theme.borderRadius} 0;
`;

// --- COMPONENT CHÍNH ---
const CSThanhToan: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('paymentPage.helmetTitle');
  }, [t]);

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <ContentCard>
          <Heading id="chinh-sach-thanh-toan">{t('paymentPage.mainHeading')}</Heading>

          <SubHeading>{t('paymentPage.methods.heading')}</SubHeading>
          <Paragraph>{t('paymentPage.methods.p1')}</Paragraph>
          <List>
            <ListItem>{t('paymentPage.methods.li1')}</ListItem>
            <ListItem>{t('paymentPage.methods.li2')}</ListItem>
          </List>

          <SubHeading>{t('paymentPage.rules.heading')}</SubHeading>
          <Paragraph>
            <Trans i18nKey="paymentPage.rules.subP1" components={{ strong: <strong /> }} />
          </Paragraph>
          <Paragraph>{t('paymentPage.rules.p2')}</Paragraph>
          
          <Paragraph>
            <Trans i18nKey="paymentPage.rules.subP3" components={{ strong: <strong /> }} />
          </Paragraph>
          <Paragraph>
            <Trans i18nKey="paymentPage.rules.p4" components={{ strong: <strong /> }} />
          </Paragraph>

          <SubHeading>{t('paymentPage.transferInfo.heading')}</SubHeading>
          <Paragraph>{t('paymentPage.transferInfo.p1')}</Paragraph>
          <InfoBlock>
            <Paragraph>
              <Trans i18nKey="paymentPage.transferInfo.bank1" components={{ strong: <strong />, br: <br />, code: <code /> }} />
            </Paragraph>
            <hr style={{border: 0, borderTop: `1px solid ${theme.colors.subtleText}`, opacity: 0.5, margin: `${theme.spacing.md} 0`}} />
            <Paragraph>
              <Trans i18nKey="paymentPage.transferInfo.bank2" components={{ strong: <strong />, br: <br />, code: <code /> }} />
            </Paragraph>
          </InfoBlock>
          <InfoBlock>
            <Paragraph>
              <Trans i18nKey="paymentPage.transferInfo.content.p1" components={{ strong: <strong />, br: <br /> }} />
            </Paragraph>
            <Paragraph>{t('paymentPage.transferInfo.content.p2')}</Paragraph>
            <Paragraph>
              <code>{t('paymentPage.transferInfo.content.p3')}</code>
            </Paragraph>
            <Paragraph style={{color: theme.colors.subtleText, fontSize: '0.9rem'}}>
              {t('paymentPage.transferInfo.content.p4')}
            </Paragraph>
          </InfoBlock>
        </ContentCard>
      </PageContainer>
    </ThemeProvider>
  );
};

export default CSThanhToan;